import { z } from 'zod';

/**
 * Flow branches — parallel drafts of a flow that can be reviewed and
 * merged independently. Backed by the `flow_branches` table from
 * migration 047.
 *
 * Lifecycle:
 *
 *   1. A flow has exactly one `default` branch (created by the
 *      backfill in migration 047 or by `POST /api/flows`).
 *   2. Editors can fork a new branch off the latest published
 *      version (`POST /api/flows/:id/branches`). Implicit branches
 *      are auto-named `<userhandle>/<utc-timestamp>`.
 *   3. Each branch carries its own `draft_json`, `draft_version`,
 *      and lease. Two engineers on different branches don't collide.
 *   4. A branch can host at most one pending review at a time
 *      (enforced by a partial unique index).
 *   5. On merge, the branch's `draft_json` is copied to the flow's
 *      `canvas_json`, a new `flow_versions` row is written, and the
 *      branch transitions to `state = 'merged'`.
 */

export const FlowBranchStateSchema = z.enum(['open', 'merged', 'abandoned']);
export type FlowBranchState = z.infer<typeof FlowBranchStateSchema>;

export const FlowBranchSchema = z.object({
  id: z.string().uuid(),
  flowId: z.string().uuid(),
  orgId: z.string().uuid(),
  name: z.string(),
  /** Published version this branch was forked from; merge uses this for 3-way conflict detection. */
  baseVersion: z.number().int(),
  /** Optimistic-concurrency token. Bumps every save; takeovers also bump. */
  draftVersion: z.number().int(),
  state: FlowBranchStateSchema,
  isDefault: z.boolean(),
  createdBy: z.string().uuid().nullable(),
  createdByName: z.string().nullable(),
  createdAt: z.string(),
  closedAt: z.string().nullable(),
});
export type FlowBranch = z.infer<typeof FlowBranchSchema>;

/**
 * Detailed branch view, including the canvas. Returned by
 * `GET /api/flows/:id/branches/:branchId` so the editor can hydrate
 * the canvas without a separate fetch.
 */
export const FlowBranchDetailSchema = FlowBranchSchema.extend({
  draftJson: z.record(z.unknown()),
});
export type FlowBranchDetail = z.infer<typeof FlowBranchDetailSchema>;

// ---- Request shapes ----

export const CreateBranchBodySchema = z.object({
  /**
   * Optional explicit name. When omitted the server generates an
   * implicit name like `karthi/2026-05-09T10-30-00`. Renaming later
   * is allowed via PATCH.
   */
  name: z.string().min(1).max(120).optional(),
  /**
   * Which published version to fork from. Defaults to the flow's
   * current `published_version`. Branching off an older version is
   * useful when reverting a regressed flow to a prior shape.
   */
  baseVersion: z.number().int().min(1).optional(),
});
export type CreateBranchBody = z.infer<typeof CreateBranchBodySchema>;

export const RenameBranchBodySchema = z.object({
  name: z.string().min(1).max(120),
});
export type RenameBranchBody = z.infer<typeof RenameBranchBodySchema>;

export const AbandonBranchBodySchema = z.object({}).strict();
export type AbandonBranchBody = z.infer<typeof AbandonBranchBodySchema>;

/**
 * Sync (rebase) a branch onto the latest published version. Useful
 * when `main` has moved on while the branch sat open. v1
 * implementation is "fast-forward only": if no steps overlap between
 * the branch and main since `base_version`, sync succeeds; otherwise
 * 409 `FLOW_BRANCH_CONFLICT` is returned and a manual resolution is
 * required.
 */
export const SyncBranchBodySchema = z.object({}).strict();
export type SyncBranchBody = z.infer<typeof SyncBranchBodySchema>;

/**
 * Merge a branch into main. v1 strategy is `replace` (the branch's
 * draft becomes the new published canvas) — same semantics as v1
 * publishFlow but keyed on branch_id. The publish gate (review
 * approvals, required checks) still applies.
 */
export const MergeBranchBodySchema = z.object({
  /** Reserved for Phase-5 force-merge by an admin. */
  override: z.boolean().optional(),
});
export type MergeBranchBody = z.infer<typeof MergeBranchBodySchema>;

// ---- Response shapes ----

export const ListFlowBranchesResponseSchema = z.object({
  branches: z.array(FlowBranchSchema),
});
export type ListFlowBranchesResponse = z.infer<typeof ListFlowBranchesResponseSchema>;

export const FlowBranchResponseSchema = z.object({
  branch: FlowBranchSchema,
});
export type FlowBranchResponse = z.infer<typeof FlowBranchResponseSchema>;

export const FlowBranchDetailResponseSchema = z.object({
  branch: FlowBranchDetailSchema,
});
export type FlowBranchDetailResponse = z.infer<typeof FlowBranchDetailResponseSchema>;

/**
 * 3-way conflict surface returned alongside `FLOW_BRANCH_CONFLICT`.
 * Describes which step ids were touched on both `main` and the
 * branch since the branch's `base_version`. v1 surface is
 * informational only — UI shows the list and asks the user to open
 * a fresh branch from the latest version.
 */
export const FlowBranchConflictSchema = z.object({
  baseVersion: z.number().int(),
  mainVersion: z.number().int(),
  conflictingStepIds: z.array(z.string()),
});
export type FlowBranchConflict = z.infer<typeof FlowBranchConflictSchema>;
